// Visual density levels — mirror of backend/src/utils/visualBudget.js.
//
// The backend is authoritative: it plans the actual placement and enforces the
// counts. This copy exists purely so the slider can label each stop with the
// figure/table counts the user will actually get.
// ponytail: keep the numbers identical to the backend VISUAL_LEVELS table.

export const VISUAL_LEVELS = {
  1: { figures: 1, tables: 0 },
  2: { figures: 2, tables: 1 },
  3: { figures: 3, tables: 2 },
  4: { figures: 4, tables: 2 },
  5: { figures: 5, tables: 3 },
}

export const DEFAULT_VISUAL_LEVEL = 3

export function clampVisualLevel(level) {
  const n = parseInt(level, 10)
  if (!Number.isFinite(n)) return DEFAULT_VISUAL_LEVEL
  return Math.max(1, Math.min(5, n))
}

// "3 figures · 2 tables" — the label under the slider.
export function describeVisualLevel(level) {
  const { figures, tables } = VISUAL_LEVELS[clampVisualLevel(level)]
  const f = `${figures} figure${figures === 1 ? '' : 's'}`
  const t = tables === 0 ? 'no tables' : `${tables} table${tables === 1 ? '' : 's'}`
  return `${f} · ${t}`
}

// ─── Browser-direct fallback support ─────────────────────────────────────────
// Used only when the backend is unreachable and the browser generates directly.
// That path makes ONE call per section, so a per-section plan is enough here;
// the backend's three-call planner is deliberately more granular.

function spread(count, slots) {
  if (slots <= 0 || count <= 0) return []
  const take = Math.min(count, slots)
  return Array.from({ length: take }, (_, i) => Math.floor((i * slots) / take))
}

export function planSectionVisuals(sectionCount, level) {
  const sections = Math.max(0, sectionCount | 0)
  if (!sections) return []
  const { figures, tables } = VISUAL_LEVELS[clampVisualLevel(level)]

  const plan = Array.from({ length: sections }, () => ({ figures: 0, tables: 0 }))
  // A chapter with fewer sections than visuals doubles up rather than silently
  // dropping the extras — a two-section chapter still gets its three diagrams,
  // and a one-section chapter still gets both its tables.
  let f = figures
  for (const i of spread(Math.min(figures, sections), sections)) { plan[i].figures += 1; f -= 1 }
  for (let i = 0; f > 0; f -= 1, i = (i + 1) % sections) plan[i].figures += 1

  // Offset tables so they do not all land on the same sections as the figures.
  const offset = sections > 2 ? 1 : 0
  let t = tables
  for (const i of spread(Math.min(tables, sections), sections)) { plan[Math.min(sections - 1, i + offset)].tables += 1; t -= 1 }
  for (let i = 0; t > 0; t -= 1, i = (i + 1) % sections) plan[i].tables += 1
  return plan
}

// The instruction text handed to one fallback call.
const COUNT_WORD = { 1: 'ONE', 2: 'TWO', 3: 'THREE', 4: 'FOUR', 5: 'FIVE' }

export function visualInstructionFor({ figures = 0, tables = 0 } = {}) {
  const word = (n) => COUNT_WORD[n] || n
  const wants = []
  if (figures > 0) wants.push(`exactly ${word(figures)} Mermaid ${figures === 1 ? 'figure' : 'figures'}`)
  if (tables  > 0) wants.push(`exactly ${word(tables)} comparison ${tables === 1 ? 'table' : 'tables'}`)
  if (!wants.length) {
    return 'VISUALS: do NOT include any figure, diagram, chart, or table in this section. Visuals are placed in other sections of this chapter.'
  }
  return `VISUALS: this section MUST include ${wants.join(' and ')}. Place them where they genuinely clarify the argument, and reference them in the surrounding prose. Write every block complete, through its closing [FIGURE_END] or [TABLE_END] marker — an unterminated block is discarded and the visual is lost.`
}
